import React from 'react';
import type {TFunction} from 'i18next';
import {
    AccountBalanceOutlined,
    AssignmentOutlined,
    AttachMoneyOutlined,
    KeyboardArrowLeftRounded,
    LocalShippingOutlined,
    MonetizationOnOutlined,
    NotesOutlined,
    PersonOutlined,
} from '@mui/icons-material';
import {routeFunctions, routes} from '../../../core/models/static/routes/app-routes';
import type {AccessLevelPackagesWithAggregates} from '../../../core/services/access-levels';
import {getScopeFrameId} from '../../components/panel-command-search';
import type {PanelEntry} from '../types';

export interface CreateCustomerPanelEntriesDeps {
    /** `useTranslation('components', {keyPrefix: 'panel-sections.sidebar.customer'})` */
    t: TFunction<'components'>;
    /** `useTranslation('components', {keyPrefix: 'panel-sections.command-search.pages'})` */
    tCmd: TFunction<'components'>;
    packages: AccessLevelPackagesWithAggregates;
}

/**
 * Builds the shared panel entry catalog for the customer-scoped sidebar / command search surfaces.
 * Mirrors `panel-sections/sidebar/customer-content` — every `hide` condition below preserves the
 * original access-level check exactly. Routes are resolved against the active `customer` scope
 * frame at render/search time instead of a fixed `customerId` prop.
 */
export const createCustomerPanelEntries = ({t, tCmd, packages}: CreateCustomerPanelEntriesDeps): PanelEntry[] => [
    {
        id: 'customer-back-to-customers',
        title: t(t => t.backToMainSidebar),
        sidebarTitle: t(t => t.backToMainSidebar),
        icon: <KeyboardArrowLeftRounded/>,
        surfaces: ['sidebar'],
        to: routes.customers.list,
    },
    {
        id: 'customer-account-info',
        title: t(t => t.accountInfo),
        sidebarTitle: t(t => t.accountInfo),
        icon: <PersonOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {
            path: [
                routes.customer.generalInformation.base,
                routes.customer.cardSummary.creditCards,
                routes.customer.cardSummary.giftCards,
            ],
            ListItemIconProps: {sx: {width: 30, height: 30}},
        },
        hide: () => !(
            packages.base.customerDetails ||
            packages.sales.manageCustomerAddresses ||
            packages.base.customerLoginLogs ||
            packages.sales.customerPromotions
        ),
        children: [
            {
                id: 'customer-account-info-general-information',
                title: tCmd(t => t.customerProfileInfo),
                sidebarTitle: t(t => t.profileInfo),
                subtitle: t(t => t.accountInfo),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.generalInformation.profile},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.generalInformation.profile(Number(id));
                },
                accessKey: 'base.customerDetails',
                hide: () => !packages.base.customerDetails,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-account-info-addresses',
                title: tCmd(t => t.customerAddresses),
                sidebarTitle: t(t => t.addresses),
                subtitle: t(t => t.accountInfo),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.generalInformation.addresses},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.generalInformation.addresses(Number(id));
                },
                accessKey: 'sales.manageCustomerAddresses',
                hide: () => !packages.sales.manageCustomerAddresses,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-account-info-payment-methods',
                title: tCmd(t => t.customerPaymentMethods),
                sidebarTitle: t(t => t.paymentMethods),
                subtitle: t(t => t.accountInfo),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {
                    path: [
                        routes.customer.generalInformation.paymentMethods,
                        routes.customer.cardSummary.creditCards,
                        routes.customer.cardSummary.giftCards,
                    ],
                },
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.generalInformation.paymentMethods(Number(id));
                },
                accessKey: 'sales.manageCustomerPaymentMethods',
                hide: () => !packages.sales.manageCustomerPaymentMethods,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-account-info-login-logs',
                title: tCmd(t => t.customerLoginLogs),
                sidebarTitle: t(t => t.loginLogs),
                subtitle: t(t => t.accountInfo),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.generalInformation.loginLogs},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.generalInformation.loginLogs(Number(id));
                },
                accessKey: 'base.customerLoginLogs',
                hide: () => !packages.base.customerLoginLogs,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-account-info-promotions',
                title: tCmd(t => t.customerPromotions),
                sidebarTitle: t(t => t.promotions),
                subtitle: t(t => t.accountInfo),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.generalInformation.promotions},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.generalInformation.promotions(Number(id));
                },
                accessKey: 'sales.customerPromotions',
                hide: () => !packages.sales.customerPromotions,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
        ],
    },
    {
        id: 'customer-price-matrices',
        title: tCmd(t => t.customerPriceMatrices),
        sidebarTitle: t(t => t.priceMatrices),
        icon: <AttachMoneyOutlined/>,
        surfaces: ['sidebar', 'command-search'],
        sidebar: {path: routes.customer.priceMatrix},
        resolveTo: (ctx) => {
            const id = getScopeFrameId(ctx.scope, 'customer');
            if (!id) return undefined;
            return routeFunctions.customer.priceMatrix(Number(id));
        },
        accessKey: 'sales.customerPriceMatrices',
        hide: () => !packages.sales.customerPriceMatrices,
        commandSearch: {providerId: 'navigation-customer', category: 'pages'},
    },
    {
        id: 'customer-notes',
        title: tCmd(t => t.customerNotes),
        sidebarTitle: t(t => t.notes),
        icon: <NotesOutlined/>,
        surfaces: ['sidebar', 'command-search'],
        sidebar: {path: routes.customer.notes},
        resolveTo: (ctx) => {
            const id = getScopeFrameId(ctx.scope, 'customer');
            if (!id) return undefined;
            return routeFunctions.customer.notes(Number(id));
        },
        accessKey: 'taskNote.notes',
        hide: () => !packages.taskNote.notes,
        commandSearch: {providerId: 'navigation-customer', category: 'pages'},
    },
    {
        id: 'customer-accounting',
        title: t(t => t.accounting),
        sidebarTitle: t(t => t.accounting),
        icon: <AccountBalanceOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.customer.accounting.base},
        hide: () => !(
            packages.sales.customerAvailableCredits ||
            packages.sales.manageInvoices ||
            packages.sales.customerAccountReceivableTransactions
        ),
        children: [
            {
                id: 'customer-accounting-available-credits',
                title: tCmd(t => t.customerAvailableCredits),
                sidebarTitle: t(t => t.availableCredits),
                subtitle: t(t => t.accounting),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.accounting.availableCredits},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.accounting.availableCredits(Number(id));
                },
                accessKey: 'sales.customerAvailableCredits',
                hide: () => !packages.sales.customerAvailableCredits,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-accounting-account-receivable-transactions',
                title: tCmd(t => t.customerAccountReceivableTransactions),
                sidebarTitle: t(t => t.accountReceivableTransactions),
                subtitle: t(t => t.accounting),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.accounting.accountReceivableTransactions},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.accounting.accountReceivableTransactions(Number(id));
                },
                accessKey: 'sales.customerAccountReceivableTransactions',
                hide: () => !packages.sales.customerAccountReceivableTransactions,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-accounting-invoices',
                title: tCmd(t => t.customerAccountingInvoices),
                sidebarTitle: t(t => t.accountingInvoices),
                subtitle: t(t => t.accounting),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.accounting.invoices.base},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.accounting.invoices.base(Number(id));
                },
                accessKey: 'sales.manageInvoices',
                hide: () => !packages.sales.manageInvoices,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
        ],
    },
    {
        id: 'customer-sales',
        title: t(t => t.sales),
        sidebarTitle: t(t => t.sales),
        icon: <MonetizationOnOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.customer.sales.base},
        hide: () => !(
            packages.sales.quotesList ||
            packages.sales.salesOrdersList ||
            packages.sales.invoicesList ||
            packages.sales.creditsList ||
            packages.sales.customerShoppingCart ||
            packages.sales.manageCustomerPaymentMethods
        ),
        children: [
            {
                id: 'customer-sales-quotes',
                title: tCmd(t => t.customerQuotes),
                sidebarTitle: t(t => t.quotes),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.sales.quotes.base},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.sales.quotes.base(Number(id));
                },
                accessKey: 'sales.quotesList',
                hide: () => !packages.sales.quotesList,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-sales-orders',
                title: tCmd(t => t.customerSalesOrders),
                sidebarTitle: t(t => t.salesOrders),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.sales.salesOrder.base},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.sales.salesOrder.base(Number(id));
                },
                accessKey: 'sales.salesOrdersList',
                hide: () => !packages.sales.salesOrdersList,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-sales-back-orders',
                title: tCmd(t => t.customerBackOrders),
                sidebarTitle: t(t => t.backOrders),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.sales.backOrder.base},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.sales.backOrder.base(Number(id));
                },
                accessKey: 'sales.salesOrdersList',
                hide: () => !packages.sales.salesOrdersList,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-sales-invoices',
                title: tCmd(t => t.customerSalesInvoices),
                sidebarTitle: t(t => t.salesInvoices),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.sales.invoices.base},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.sales.invoices.base(Number(id));
                },
                accessKey: 'sales.invoicesList',
                hide: () => !packages.sales.invoicesList,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-sales-credits',
                title: tCmd(t => t.customerCredits),
                sidebarTitle: t(t => t.credits),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.sales.credits.base},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.sales.credits.base(Number(id));
                },
                accessKey: 'sales.creditsList',
                hide: () => !packages.sales.creditsList,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-sales-shopping-cart',
                title: tCmd(t => t.customerShoppingCart),
                sidebarTitle: t(t => t.shoppingCart),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.sales.shoppingCart},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.sales.shoppingCart(Number(id));
                },
                accessKey: 'sales.customerShoppingCart',
                hide: () => !packages.sales.customerShoppingCart,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
        ],
    },
    {
        id: 'customer-forms',
        title: t(t => t.forms),
        sidebarTitle: t(t => t.forms),
        icon: <AssignmentOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.customer.forms.responses},
        hide: () => !packages.bizForm.formResponsesList,
        children: [
            {
                id: 'customer-form-responses',
                title: tCmd(t => t.customerFormResponses),
                sidebarTitle: t(t => t.formsResponses),
                subtitle: t(t => t.forms),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.forms.responses},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.forms.responses(Number(id));
                },
                accessKey: 'bizForm.formResponsesList',
                hide: () => !packages.bizForm.formResponsesList,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
        ],
    },
    {
        id: 'customer-freight',
        title: t(t => t.freight),
        sidebarTitle: t(t => t.freight),
        icon: <LocalShippingOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.customer.shippingFees.base},
        hide: () => !(
            packages.sales.customerFreightFees ||
            packages.sales.customerFreeFreightFees
        ),
        children: [
            {
                id: 'customer-freight-shipping-fees',
                title: tCmd(t => t.customerShippingFees),
                sidebarTitle: t(t => t.shippingFees),
                subtitle: t(t => t.freight),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.shippingFees.list},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.shippingFees.list(Number(id));
                },
                accessKey: 'sales.customerFreightFees',
                hide: () => !packages.sales.customerFreightFees,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
            {
                id: 'customer-freight-free-shipping-fees',
                title: tCmd(t => t.customerFreeShippingFees),
                sidebarTitle: t(t => t.freeShippingFees),
                subtitle: t(t => t.freight),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.customer.shippingFees.settings},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'customer');
                    if (!id) return undefined;
                    return routeFunctions.customer.shippingFees.settings(Number(id));
                },
                accessKey: 'sales.customerFreeFreightFees',
                hide: () => !packages.sales.customerFreeFreightFees,
                commandSearch: {providerId: 'navigation-customer', category: 'pages'},
            },
        ],
    },
];

export default createCustomerPanelEntries;
